import {
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Grid,
  Popover,
  Typography,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import { Add, Cancel, NavigateBefore, NavigateNext, Search } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useClassroomActions } from "../../../hooks/useClassroomActions";

const ITEMS_PER_PAGE = 10;
let OFFSET = 0;

type RouteParams = {
  classroom_id: string;
};

interface ResourcesProps {
  id: string | number;
  resourceAnchorEl: HTMLElement | null;
  setResourceAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  onselect: (resource: string) => void;
}

export default function ResourcesView({ id, resourceAnchorEl, setResourceAnchorEl, onselect }: ResourcesProps) {
  
  const { classroom_id } = useParams<RouteParams>();
  const { getImageResources, getImageResourcesByTitle } = useClassroomActions();

  const [loadingResources, setLoadingResources] = useState(false);
  const [availableResources, setAvailableResources] = useState<[string, string][]>([]);
  const [filteredResources, setFilteredResources] = useState<[string, string][]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const handleAddResource = (url: string) => {
    onselect(url);
    handleClose();
  };

  const handleClose = () => {
    setResourceAnchorEl(null);
  };

  const handleLocalSearch = () => {
    const localFiltered = availableResources.filter(([title]) =>
      title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredResources(localFiltered);
    setPage(1);
  };

  const handleRemoteSearch = async () => {
    if (!searchQuery.trim() || !classroom_id) return;

    setLoadingResources(true);
    try {
      OFFSET = 0;
      setHasMore(true);
      const fetched = await getImageResourcesByTitle(Number(classroom_id), searchQuery);
      setAvailableResources(fetched ?? []);
      setFilteredResources(fetched ?? []);
    } catch {
      setFilteredResources([]);
    } finally {
      setLoadingResources(false);
      setPage(1);
    }
  };

  const fetchDefaultResources = async (newBatch = false) => {
    if (loadingResources || !classroom_id) return;

    setLoadingResources(true);
    try {
      const fetched = await getImageResources(Number(classroom_id), OFFSET, newBatch);

      if (newBatch) {
        setAvailableResources((prev) => [...prev, ...fetched]);
        setFilteredResources((prev) => [...prev, ...fetched]);
        OFFSET += fetched.length;
      } else {
        setAvailableResources(fetched);
        setFilteredResources(fetched);
        OFFSET = fetched.length;
      }

      if (fetched.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }
    } catch {
      setAvailableResources([]);
      setFilteredResources([]);
    } finally {
      setLoadingResources(false);
      setPage(1);
    }
  };

  useEffect(() => {
    OFFSET = 0;
    setHasMore(true);
    fetchDefaultResources();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      OFFSET = 0;
      setHasMore(true);
      fetchDefaultResources();
    } else {
      handleLocalSearch();
    }
  }, [searchQuery]);

  const paginatedResources = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredResources.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredResources, page]);

  const loadMoreDisabled = hasMore && !loadingResources;

  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);

  return (
    <Popover
      key={id}
      anchorEl={resourceAnchorEl}
      open={Boolean(resourceAnchorEl)}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { width: 320, height: 320, p: 2, display: "flex", flexDirection: "column" } }}
    >
      <TextField
        fullWidth
        placeholder="Search resources"
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleRemoteSearch} title="Search in all resources">
                <Search />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleRemoteSearch();
          }
        }}
      />

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {loadingResources ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Loading...</Typography>
          </Box>
        ) : filteredResources.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No resources found.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            {paginatedResources.map(([title, url]) => (
              <Grid size={{ xs: 4 }} key={url}>
                <img
                  onClick={() => handleAddResource(url)}
                  src={url}
                  alt={title}
                  style={{
                    width: "100%",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {(totalPages > 1 || hasMore) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 1,
            borderTop: "1px solid #e0e0e0",
            mt: 1,
          }}
        >
          
          <Typography
            variant="body2"
            sx={{
              cursor: loadMoreDisabled ? "not-allowed" : "pointer",
              color: loadMoreDisabled ? "text.disabled" : "primary.main",
              pointerEvents: loadMoreDisabled ? "none" : "auto",
              opacity: loadMoreDisabled ? 0.6 : 1,
            }}
            onClick={() => !loadMoreDisabled && fetchDefaultResources(true)}
          >
            Load more
          </Typography>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
              >
                <NavigateBefore fontSize="small" />
              </IconButton>
              <Typography variant="body2">
                {page}/{totalPages}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
              >
                <NavigateNext fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Popover>
  );
}
